/**
 * usePostMessage - postMessage 通信組合式函數
 *
 * 負責 Host ↔ Cocos 游戲 iframe 的雙向通信。
 * shared-web (Nuxt popup iframe) 通信已移除；所有彈窗走 native host 元件。
 */
import { toRaw, isProxy, watch, type Ref } from 'vue'
import { type HostMessageAction, createHostMessage, isValidHostMessage } from '../useHostBridge'
import { APP_ENV, API_BASE, EXTERNAL_MODULES_BASE } from '../runtimeConfig'
import type { UserInfo } from '../authService'
import { i18n } from '../i18n'
import {
  useMessageQueueStore,
  isAckMessage,
  createQueueMessage,
  type QueueMessage,
  type EnqueueOptions,
} from '../stores/messageQueue'

/** `GAME_READY` 的 payload（Cocos → Host）。`hostLoading: true` = 這款走新 loading 流程（遮罩別收）。 */
export interface GameReadyPayload {
  hostLoading?: boolean
}

/** `LOAD_ERROR` 的 payload（Cocos → Host）。`code` 目前只有 `BOOTSTRAP_FAILED`，之後再擴充。 */
export interface LoadErrorPayload {
  code?: string
  message?: string
}

export interface PostMessageHandlers {
  onOpenModal: (modalKey: string) => void
  onAuthInvalid: () => void
  /** 引擎起來、listener 掛好。`payload.hostLoading` 決定 host 收不收遮罩（見 docs/LOADING_PROTOCOL.md 2.6）。 */
  onGameReady: (payload: GameReadyPayload) => void
  onExitGame?: () => void
  // ── Loading 流程（新款限定；舊款不會送，handler 可不接） ──
  /** 資源載完、可以開始遊戲 → host 補滿假進度 / 換開始鈕（或 kill switch 下直接送 LOADING_CLOSED） */
  onLoadComplete?: () => void
  /** 載入失敗 */
  onLoadError?: (payload: LoadErrorPayload) => void
  /** 載入期心跳（每 5s）→ host 拿來 re-arm 卡住偵測 */
  onLoadAlive?: () => void
}

export interface PostMessageDeps {
  gameId: Ref<string | null>
  gameUrl: Ref<string | null>
  gameFrame: Ref<HTMLIFrameElement | null>
  authToken: Ref<string | null>
  authUserInfo: Ref<UserInfo | null>
  authReady: Ref<boolean>
  currency?: Ref<string | null | undefined>
}

/**
 * 將代理物件轉為可 postMessage 的普通物件
 */
function toPlain<T>(v: T): T {
  const raw = isProxy(v as any) ? toRaw(v as any) : v
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(raw)
    } catch {
      // fall through
    }
  }
  return JSON.parse(JSON.stringify(raw))
}

/**
 * 安全發送 postMessage，失敗時 log 並 rethrow
 */
function safePostMessage(target: Window, message: any, origin: string): boolean {
  try {
    const safeMessage = toPlain(message)
    target.postMessage(safeMessage, origin)
    return true
  } catch (e) {
    console.error('[web][HostBridge] postMessage failed:', e, { origin, action: message?.action })
    throw e
  }
}

/**
 * 從 URL 提取 origin
 */
function extractOrigin(url: string | null): string | null {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    if (url.startsWith('/') && typeof window !== 'undefined') {
      return window.location.origin
    }
    return null
  }
}

export function usePostMessage(deps: PostMessageDeps, handlers: PostMessageHandlers) {
  const { gameId, gameUrl, gameFrame, authToken, authUserInfo, authReady } = deps

  function getGameOrigin(): string | null {
    return extractOrigin(gameUrl.value)
  }

  const messageQueue = useMessageQueueStore()

  // ── 消息隊列初始化 ──────────────────────────────────────────────

  function initMessageQueue(options?: {
    onMessageFailed?: (message: QueueMessage, error: string) => void
  }) {
    if (messageQueue.initialized) return

    messageQueue.init({
      send: (message: QueueMessage) => {
        _sendToGameFrame(message)
      },
      onMessageFailed: (message, error) => {
        console.error('[web][HostBridge] Message failed:', message.action, message.msgId, error)
        options?.onMessageFailed?.(message, error)
      },
    })
  }

  // ── 內部發送 ────────────────────────────────────────────────────

  function _sendToGameFrame(message: QueueMessage): boolean {
    if (!gameFrame.value?.contentWindow || !gameId.value) {
      console.warn('[web][HostBridge] send skipped (no game frame):', message.action, message.msgId)
      return false
    }
    const origin = getGameOrigin()
    if (!origin) {
      console.warn('[web][HostBridge] send skipped (no origin):', message.action, message.msgId)
      return false
    }
    console.log('[web][HostBridge → Cocos]', message.action, message.msgId, message.payload)
    safePostMessage(gameFrame.value.contentWindow, message, origin)
    return true
  }

  // ── 直接發送（不走佇列） ─────────────────────────────────────────

  function postToGame(action: HostMessageAction, payload: Record<string, unknown>) {
    if (!gameFrame.value?.contentWindow || !gameId.value) return
    const origin = getGameOrigin()
    if (!origin) return
    const payloadWithLang = { ...payload, l: i18n.global.locale.value }
    const message = createHostMessage(action, payloadWithLang)
    console.log('[web][HostBridge → Cocos]', action, message.payload)
    safePostMessage(gameFrame.value.contentWindow, message, origin)
  }

  // ── 透過佇列發送 ─────────────────────────────────────────────────

  function enqueueToGame(
    action: string,
    payload: Record<string, unknown>,
    options?: EnqueueOptions & { requiresAck?: boolean },
  ) {
    const { requiresAck = true, ...enqueueOptions } = options ?? {}
    const payloadWithLang = { ...payload, l: i18n.global.locale.value }
    const message = createQueueMessage(action, payloadWithLang, requiresAck)
    messageQueue.enqueue(message, enqueueOptions)
  }

  // ── Token 發送 ───────────────────────────────────────────────────

  function sendTokenTo(targetWindow: Window, origin: string): boolean {
    if (!authReady.value || !authToken.value) return false
    const payload: Record<string, unknown> = { token: authToken.value }
    if (authUserInfo.value) payload.userInfo = toPlain(authUserInfo.value)
    if (deps.currency?.value) payload.currency = deps.currency.value
    payload.l = i18n.global.locale.value
    safePostMessage(targetWindow, createHostMessage('SET_TOKEN', payload), origin)
    return true
  }

  // 死鎖防護：refresh 時 Cocos 走 cache 啟動較快，可能在 host 拿到 token 前就送 REQUEST_TOKEN。
  // 此時 sendTokenTo 因 authReady=false 發不出去 → 記住來源，待 authReady 轉 true 補發一次。
  // 否則 Cocos 等 SET_TOKEN、host 的 [authReady,gameLoaded] watch 等 GAME_READY（要先收到 token 才會送）
  // → 互等死鎖（首次/清 cache 載入較慢、token 先到，故不會重現）。
  let pendingTokenTarget: { win: Window; origin: string } | null = null
  watch(authReady, (ready) => {
    if (ready && pendingTokenTarget) {
      if (sendTokenTo(pendingTokenTarget.win, pendingTokenTarget.origin)) {
        pendingTokenTarget = null
      }
    }
  })

  function sendLanguageTo(targetWindow: Window, origin: string) {
    const payload: Record<string, unknown> = { l: i18n.global.locale.value }
    safePostMessage(targetWindow, createHostMessage('SET_LANGUAGE', payload), origin)
  }

  // ── 執行期設定（SET_CONFIG） ─────────────────────────────────────
  // env = config.json 的 env（APP_ENV，環境標記）；apiBaseUrl = config.json 的 api（API_BASE）；
  // externalModulesBaseDomain = 同網域 /ext-module；gameCode = host 解析出的 gameId
  // （**一律小寫** —— `readGameId()` 在唯一解析點就轉好了，URL 帶 `?gameId=XLTB` 也會是 'xltb'）。
  // 非機密、也非語系相關，故不附 l。
  function sendConfigTo(targetWindow: Window, origin: string): boolean {
    const payload: Record<string, unknown> = {
      env: APP_ENV,
      apiBaseUrl: API_BASE,
      externalModulesBaseDomain: EXTERNAL_MODULES_BASE,
      gameCode: gameId.value ?? '',
    }
    console.log('[web][HostBridge → Cocos]', 'SET_CONFIG', payload)
    safePostMessage(targetWindow, createHostMessage('SET_CONFIG', payload), origin)
    return true
  }

  function sendConfigToGame() {
    if (!gameFrame.value?.contentWindow || !gameId.value) return
    const origin = getGameOrigin()
    if (!origin) return
    sendConfigTo(gameFrame.value.contentWindow, origin)
  }

  /**
   * `LOADING_CLOSED`（Host → Cocos）：遮罩已卸、Cocos 可以 startGame()。
   * 🛑 **走佇列 + requiresAck**（3s timeout、重試 2 次）—— 這則不能掉：掉了遊戲永遠不會開始。
   *    Cocos 端已做冪等（重送只回 ACK、不重複 startGame），所以重試安全。
   *    最終失敗 → App.vue 的 onMessageFailed 會 reload iframe（同 MODAL_CLOSED）。
   * 🛑 **只對 `hostLoading` 的新款送**（由 useHostLoading 把關）；舊款永遠不會收到這則。
   */
  function sendLoadingClosedToGame() {
    enqueueToGame('LOADING_CLOSED', {}, { requiresAck: true })
  }

  function sendTokenToGame() {
    if (!gameFrame.value?.contentWindow || !gameId.value) return
    const origin = getGameOrigin()
    if (!origin) return
    sendTokenTo(gameFrame.value.contentWindow, origin)
  }

  function sendSpinToGame() {
    postToGame('SPIN', {})
  }

  // ── 通道控制 ─────────────────────────────────────────────────────

  function setChannelReady(value: boolean) {
    messageQueue.setReady(value)
  }

  function handleGameReload() {
    messageQueue.handleReload()
  }

  // ── 消息監聽 ─────────────────────────────────────────────────────

  function handlePostMessage(event: MessageEvent) {
    if (!gameId.value) return

    const gameOrigin = getGameOrigin()
    if (!gameOrigin || event.origin !== gameOrigin) return

    // ACK 回執
    if (isAckMessage(event.data)) {
      console.log('[web][HostBridge ← Cocos] ACK', event.data.payload.msgId, event.data.payload.status)
      messageQueue.handleAck(event.data)
      return
    }

    if (!isValidHostMessage(event.data)) return

    const { action, payload } = event.data
    console.log('[web][HostBridge ← Cocos]', action, payload)
    switch (action) {
      case 'OPEN_MODAL':
        handlers.onOpenModal(payload.modalKey as string)
        break
      case 'REQUEST_TOKEN':
        // Cocos 請求 token（如重連後主動索取）。token 還沒好 → 記住來源，authReady 後補發（見上方 watch）。
        if (event.source) {
          const win = event.source as Window
          if (!sendTokenTo(win, event.origin)) {
            pendingTokenTarget = { win, origin: event.origin }
          }
        }
        break
      case 'REQUEST_LANGUAGE':
        if (event.source) {
          sendLanguageTo(event.source as Window, event.origin)
        }
        break
      case 'REQUEST_CONFIG':
        // Cocos 請求執行期設定（漏收 / reset 後重拉）。config 不依賴 authReady，直接回。
        if (event.source) {
          sendConfigTo(event.source as Window, event.origin)
        }
        break
      case 'AUTH_INVALID':
        handlers.onAuthInvalid()
        break
      case 'GAME_READY':
        handlers.onGameReady((payload ?? {}) as GameReadyPayload)
        break
      case 'EXIT_GAME':
        handlers.onExitGame?.()
        break
      // ── Loading 流程（docs/LOADING_PROTOCOL.md）──
      case 'LOAD_COMPLETE':
        handlers.onLoadComplete?.()
        break
      case 'LOAD_ERROR':
        handlers.onLoadError?.((payload ?? {}) as LoadErrorPayload)
        break
      case 'LOAD_ALIVE':
        handlers.onLoadAlive?.()
        break
      default:
        // 🛑 不要改回靜默略過：Cocos 改共用層時任何多送 / 改名的訊息要**當場在 console 現形**
        //    （之前 `{ type:'PROGRESS' }` 那種格式被靜默丟掉、兩邊對了半天）。只 warn、不拋。
        console.warn('[web][HostBridge ← Cocos] 未知 action（已略過）:', action, payload)
    }
  }

  function startListening() {
    window.addEventListener('message', handlePostMessage)
  }

  function stopListening() {
    window.removeEventListener('message', handlePostMessage)
  }

  return {
    sendTokenToGame,
    sendConfigToGame,
    sendSpinToGame,
    sendLoadingClosedToGame,
    initMessageQueue,
    enqueueToGame,
    setChannelReady,
    handleGameReload,
    startListening,
    stopListening,
  }
}
